import {
  PreparedFolder,
  PreparedItem,
  PreparedWorkItem,
} from '@/models/item.models';
import { computed, ComputedRef } from 'vue';
import {
  OrganizationBase,
  OrganizationMembership,
  Team,
  User,
} from '@/models/user.models';
import { CopyFields, GithubInstallation, SlackInstallation } from '@/api';
import uniq from 'lodash-es/uniq';
import { useAuthStore } from './authStore';
import { useUserStore } from './userStore';
import { useOrganizationStore } from './organizationStore';
import { useItemStore } from './itemStore';
import { useGeneralStore } from './generalStore';
import Notify from '@/utils/app-notification';
import { isNil } from 'lodash-es';
import { useBoardStore } from './boardStore';
import { Board } from '@/api';
import { PreparedItemRequest } from '@/models/item-request.models';
import {
  ItemRequestGroup,
  FindItemsData,
  Label,
  Lane,
  Organization,
  Priority,
  UserLabelGroup,
  WorkType,
  WorkItem,
  Folder,
  ItemType,
  FindNotificationsData,
} from '@/api';

interface OrganizationData {
  user: ComputedRef<User | null>;
  userMembership: ComputedRef<OrganizationMembership | undefined>;
  isAdmin: ComputedRef<boolean>;
  isOwner: ComputedRef<boolean>;
  isDevStrideAdmin: ComputedRef<boolean>;
  orgMembershipCache: ComputedRef<OrganizationMembership[]>;
  activeModule: ComputedRef<string>;
  organization: ComputedRef<Organization | null>;
  loadingOrganizations: ComputedRef<boolean>;
  workTypes: ComputedRef<Array<WorkType>>;
  allRootFolders: ComputedRef<Array<PreparedFolder>>;
  boardCache: ComputedRef<Array<Board>>;
  allOrganizations: ComputedRef<Array<OrganizationBase>>;
  lastDrawerWorkItem: ComputedRef<PreparedItem | null>;
  priorities: ComputedRef<Array<Priority> | undefined>;
  labels: ComputedRef<Array<Label> | undefined>;
  addBlocker: ComputedRef<{
    visible: boolean;
    workItem: PreparedWorkItem | null;
  }>;
  userLabelGroups: ComputedRef<Array<UserLabelGroup>>;
  githubInstallations: ComputedRef<GithubInstallation[]>;
  githubInstallationLoading: ComputedRef<boolean>;
  slackInstallations: ComputedRef<SlackInstallation[]>;
  slackInstallationLoading: ComputedRef<boolean>;
  teamCache: ComputedRef<Array<Team>>;
  itemsCaches: ComputedRef<{ [key: string]: Array<PreparedItem> }>;
  toolbarCollapsed: ComputedRef<boolean>;
  trialSubscription: ComputedRef<boolean>;
  daysLeftInTrial: ComputedRef<number | null>;
  paymentFailed: ComputedRef<boolean>;
  paymentFailAccessRevoked: ComputedRef<boolean>;
  subscriptionCancelled: ComputedRef<boolean>;
  storageLimitAlertTriggerIncrement: ComputedRef<number>;
  itemRequests: ComputedRef<Array<PreparedItemRequest>>;
  itemRequestGroups: ComputedRef<Array<ItemRequestGroup>>;
  orgHasWorkTypesUsingPointEstimates: ComputedRef<boolean>;
  orgHasWorkTypesUsingTimeEstimates: ComputedRef<boolean>;
  drawerOpenAnimationDisabled: ComputedRef<boolean>;
}

export function organizationData(): OrganizationData {
  const generalStore = useGeneralStore();
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const organizationStore = useOrganizationStore();
  const itemStore = useItemStore();
  const boardStore = useBoardStore();
  return {
    user: computed(() => authStore.user),
    userMembership: computed(() => authStore.currentUserMembership),
    isAdmin: computed(() => authStore.isAdmin),
    isOwner: computed(() => authStore.isOwner),
    isDevStrideAdmin: computed(() => authStore.isDevStrideAdmin),
    orgMembershipCache: computed(() => userStore.orgMembershipCache),
    activeModule: computed(() => generalStore.activeModule),
    organization: computed(() => organizationStore.activeOrganization),
    loadingOrganizations: computed(
      () => organizationStore.loadingOrganizations
    ),
    allRootFolders: computed(() => itemStore.rootFolders),
    workTypes: computed(() =>
      organizationStore.workTypes.filter((wt) => !wt.archived)
    ),
    boardCache: computed(() => boardStore.allCyclesAndBoards),
    allOrganizations: computed(() => organizationStore.allOrganizations),
    lastDrawerWorkItem: computed(() => itemStore.lastDrawerWorkItem),
    addBlocker: computed(() => itemStore.addBlocker),

    priorities: computed(() => organizationStore.priorities),
    labels: computed(() => organizationStore.labels),
    userLabelGroups: computed(() => organizationStore.userLabelGroups),
    githubInstallations: computed(() => organizationStore.githubInstallations),
    githubInstallationLoading: computed(
      () => organizationStore.githubInstallationLoading
    ),
    slackInstallations: computed(() => organizationStore.slackInstallations),
    slackInstallationLoading: computed(
      () => organizationStore.slackInstallationLoading
    ),
    teamCache: computed(() => userStore.teamCache),
    itemsCaches: computed(() => itemStore.itemsCaches),
    toolbarCollapsed: computed(() => generalStore.toolbarCollapsed),

    trialSubscription: computed(() => organizationStore.trialSubscription),
    daysLeftInTrial: computed(() => organizationStore.daysLeftInTrial),
    paymentFailed: computed(() => organizationStore.paymentFailed),
    paymentFailAccessRevoked: computed(
      () => organizationStore.paymentFailAccessRevoked
    ),
    subscriptionCancelled: computed(
      () => organizationStore.subscriptionCancelled
    ),
    storageLimitAlertTriggerIncrement: computed(
      () => organizationStore.storageLimitAlertTriggerIncrement
    ),
    itemRequests: computed(() => organizationStore.itemRequests),
    itemRequestGroups: computed(() => organizationStore.itemRequestGroups),
    orgHasWorkTypesUsingPointEstimates: computed(() => {
      return organizationStore.workTypes
        .filter((wt) => !wt.archived)
        .some((wt) => wt.enablePointEffort);
    }),
    orgHasWorkTypesUsingTimeEstimates: computed(() => {
      return organizationStore.workTypes
        .filter((wt) => !wt.archived)
        .some((wt) => wt.enableTimeEstimates);
    }),
    drawerOpenAnimationDisabled: computed(
      () => itemStore.drawerOpenAnimationDisabled
    ),
  };
}

export async function prepareWorkItems(
  workItems: Array<Folder | WorkItem>
): Promise<Array<PreparedItem>> {
  const userStore = useUserStore();
  const organizationStore = useOrganizationStore();
  const boardStore = useBoardStore();

  await prefetchTeams(workItems);
  await prefetchBoards(workItems);

  const result = await Promise.all(
    workItems.map((item) => {
      const w = {
        author: userStore.orgMembershipCache.find(
          (u: OrganizationMembership) => u.userId === item.authorUsername
        ) as OrganizationMembership,
        participantUsers: userStore.orgMembershipCache.filter(
          (u: OrganizationMembership) => {
            return item.participants?.includes(u.userId);
          }
        ) as Array<OrganizationMembership>,
        ...item,
      } as PreparedItem;
      if (w.itemType === ItemType.WORKITEM) {
        if (w.assigneeUsername) {
          w.assignee = userStore.orgMembershipCache.find(
            (u: OrganizationMembership) => u.userId === w.assigneeUsername
          );
        }
        if (w.laneId) {
          const allLanes = organizationStore.activeOrganization?.lanes;
          w.lane = allLanes?.find((l: Lane) => l.id === w.laneId) as
            | Lane
            | undefined;
        } else {
          w.lane = undefined;
        }
        // if (w.parents) {
        //   w.parent = w.parents.find((p) => p.number === w.parentNumber);
        // }
        w.labels = w.labels || [];

        // Team
        if (w.teamId) {
          w.team =
            userStore.teamCache.find((t: Team) => t.id === w.teamId) ||
            'private';
        } else {
          w.team = undefined;
        }

        // Board
        if (w.boardId) {
          w.board = boardStore.allCyclesAndBoards.find(
            (b) => b.id === w.boardId
          );
        } else {
          w.board = undefined;
        }

        // Priority
        if (w.priorityId) {
          w.priority = organizationStore.priorities?.find(
            (p: Priority) => p.id === w.priorityId
          );
        } else {
          w.priority = undefined;
        }
      }
      if (w.itemType === ItemType.FOLDER) {
        // Team
        // TODO: Restore when restrictedToTeams is added to PreparedFolder type
        // const teamIds = w.restrictedToTeams;
        // if (teamIds) {
        //   w.teams = userStore.teamCache.filter((t: Team) =>
        //     teamIds.includes(t.id),
        //   );
        // }
      }

      if (w.itemType === ItemType.WORKITEM) {
        w.workType = organizationStore.workTypes.find(
          (wt: WorkType) => wt.id === w.workTypeId
        ) as WorkType;
      }

      w.baseItemAggregations = getLeafAggregations(w, 'aggregations');
      w.baseArchivedItemAggregations = getLeafAggregations(
        w,
        'archivedAggregations'
      );
      w.totalItemCount = getTotalItemCountFromAggregations(w.aggregations);
      w.totalArchivedItemCount = getTotalItemCountFromAggregations(
        w.archivedAggregations
      );

      w.canHaveChildren =
        w.itemType === ItemType.FOLDER || w.workType?.children.length > 0;

      return w;
    })
  );
  return result;
}

function getLeafAggregations(
  w: PreparedItem,
  aggregationType: 'aggregations' | 'archivedAggregations'
) {
  const organizationStore = useOrganizationStore();
  if (!w[aggregationType]) {
    return {
      totalOptimisticEstimate: 0,
      totalLikelyEstimate: 0,
      totalPessimisticEstimate: 0,
      totalPointEffort: 0,
      totalTimeSpentSeconds: 0,
      totalOptimisticEstimateCompleted: 0,
      totalLikelyEstimateCompleted: 0,
      totalPessimisticEstimateCompleted: 0,
      totalPointEffortCompleted: 0,
      totalTimeSpentSecondsCompleted: 0,
      totalItemCount: 0,
      totalPlannedItemCount: 0,
      totalCompletedItemCount: 0,
      totalEstimate: 0,
      totalEstimateCompleted: 0,
      totalEstimateSd: 0,
      totalEstimateSdCompleted: 0,
    };
  }
  const allChildWorkTypeIds = Object.keys(w[aggregationType]);
  const allWorkTypes = organizationStore.workTypes;
  const applicableWorkTypeIds = allChildWorkTypeIds.filter((id) => {
    const workType = allWorkTypes.find((wt) => wt.id === id);
    if (!workType) return false;
    return !workType.children.length;
  });
  return applicableWorkTypeIds.reduce(
    (acc, id) => {
      const workTypeAggregations = w[aggregationType][id];
      acc.totalOptimisticEstimate +=
        workTypeAggregations.totalOptimisticEstimate;
      acc.totalLikelyEstimate += workTypeAggregations.totalLikelyEstimate;
      acc.totalPessimisticEstimate +=
        workTypeAggregations.totalPessimisticEstimate;
      acc.totalPointEffort += workTypeAggregations.totalPointEffort;
      acc.totalTimeSpentSeconds += workTypeAggregations.totalTimeSpentSeconds;
      acc.totalOptimisticEstimateCompleted +=
        workTypeAggregations.totalOptimisticEstimateCompleted;
      acc.totalLikelyEstimateCompleted +=
        workTypeAggregations.totalLikelyEstimateCompleted;
      acc.totalPessimisticEstimateCompleted +=
        workTypeAggregations.totalPessimisticEstimateCompleted;
      acc.totalPointEffortCompleted +=
        workTypeAggregations.totalPointEffortCompleted;
      acc.totalTimeSpentSecondsCompleted +=
        workTypeAggregations.totalTimeSpentSecondsCompleted;
      acc.totalItemCount += workTypeAggregations.totalItemCount;
      acc.totalPlannedItemCount += workTypeAggregations.totalPlannedItemCount;
      acc.totalCompletedItemCount +=
        workTypeAggregations.totalCompletedItemCount;
      acc.totalEstimate += workTypeAggregations.totalEstimate;
      acc.totalEstimateCompleted += workTypeAggregations.totalEstimateCompleted;
      acc.totalEstimateSd += workTypeAggregations.totalEstimateSd;
      acc.totalEstimateSdCompleted +=
        workTypeAggregations.totalEstimateSdCompleted;
      return acc;
    },
    {
      totalOptimisticEstimate: 0,
      totalLikelyEstimate: 0,
      totalPessimisticEstimate: 0,
      totalPointEffort: 0,
      totalTimeSpentSeconds: 0,
      totalOptimisticEstimateCompleted: 0,
      totalLikelyEstimateCompleted: 0,
      totalPessimisticEstimateCompleted: 0,
      totalPointEffortCompleted: 0,
      totalTimeSpentSecondsCompleted: 0,
      totalItemCount: 0,
      totalPlannedItemCount: 0,
      totalCompletedItemCount: 0,
      totalEstimate: 0,
      totalEstimateCompleted: 0,
      totalEstimateSd: 0,
      totalEstimateSdCompleted: 0,
    } as PreparedItem['baseItemAggregations']
  );
}

function getTotalItemCountFromAggregations(
  aggregations: PreparedItem['aggregations']
) {
  return Object.keys(aggregations).reduce((acc, id) => {
    const workTypeAggregations = aggregations[id];
    acc += workTypeAggregations.totalItemCount;
    return acc;
  }, 0);
}

// Track if all teams have been fetched to avoid redundant API calls
let allTeamsFetched = false;

// Track boards we've already fetched or are currently fetching
const fetchedBoardIds = new Set<string>();
let pendingBoardFetch: Promise<void> | null = null;

/**
 * Resets the prefetch caches for teams and boards.
 * Should be called when switching organizations or logging out
 * to ensure fresh data is fetched for the new context.
 */
export function resetPrefetchCaches(): void {
  allTeamsFetched = false;
  fetchedBoardIds.clear();
  pendingBoardFetch = null;
}

async function prefetchTeams(workItems: Array<WorkItem | Folder>) {
  const userStore = useUserStore();
  const allTeamIds = uniq(workItems.map((i) => (i as WorkItem).teamId)).filter(
    Boolean
  );
  const missingTeamIds = allTeamIds.filter(
    (id) => !userStore.teamCache.find((t: Team) => t.id === id)
  );

  if (missingTeamIds.length && !allTeamsFetched) {
    // Batch fetch: Load all teams at once instead of individual API calls
    await userStore.getAllTeams();
    allTeamsFetched = true;
  }
}

async function prefetchBoards(workItems: Array<WorkItem | Folder>) {
  const boardStore = useBoardStore();

  const boardIdsToFetch = new Set<Board['id']>();
  workItems.forEach((item) => {
    if (item.itemType === ItemType.WORKITEM) {
      const boardId = item.boardId;
      if (!boardId) return; // not set
      if (fetchedBoardIds.has(boardId)) return; // already fetched or fetching
      if (boardStore.allCyclesAndBoards.find((b) => b.id === boardId)) {
        fetchedBoardIds.add(boardId); // mark as fetched
        return;
      }
      boardIdsToFetch.add(boardId);
    }
  });

  if (!boardIdsToFetch.size) return;

  // Mark these as being fetched to prevent duplicate requests
  boardIdsToFetch.forEach((id) => fetchedBoardIds.add(id));

  // If there's already a pending fetch, wait for it first to batch requests
  if (pendingBoardFetch) {
    await pendingBoardFetch;
  }

  pendingBoardFetch = boardStore
    .getBoards({
      ids: [...boardIdsToFetch],
      limit: boardIdsToFetch.size,
    })
    .then(() => {
      pendingBoardFetch = null;
    });

  await pendingBoardFetch;
}

export function filtersToQueryParams(
  filters: NonNullable<FindItemsData['query']>
): Partial<
  Record<keyof NonNullable<FindItemsData['query']>, string | boolean | number>
> {
  const params: Partial<
    Record<keyof NonNullable<FindItemsData['query']>, string | boolean | number>
  > = {};

  if (filters.itemType) {
    params.itemType = filters.itemType;
  }
  if (filters.q) {
    params['q'] = filters.q;
  }
  if (filters.hasNoParent) {
    params['hasNoParent'] = filters.hasNoParent;
  }
  if (filters.isBacklog) {
    params['isBacklog'] = filters.isBacklog;
  }
  if (filters.includeArchived) {
    params['includeArchived'] = filters.includeArchived;
  }
  if (filters.dueDateGTE) {
    params['dueDateGTE'] = filters.dueDateGTE;
  }
  if (filters.dueDateLTE) {
    params['dueDateLTE'] = filters.dueDateLTE;
  }
  if (filters.startDateGTE) {
    params['startDateGTE'] = filters.startDateGTE;
  }
  if (filters.startDateLTE) {
    params['startDateLTE'] = filters.startDateLTE;
  }
  if (filters.completedAtGTE) {
    params['completedAtGTE'] = filters.completedAtGTE;
  }
  if (filters.completedAtLTE) {
    params['completedAtLTE'] = filters.completedAtLTE;
  }
  if (!isNil(filters.timeSpentSecondsGTE)) {
    params['timeSpentSecondsGTE'] = filters.timeSpentSecondsGTE;
  }
  if (!isNil(filters.timeSpentSecondsLTE)) {
    params['timeSpentSecondsLTE'] = filters.timeSpentSecondsLTE;
  }
  if (typeof filters.isDone === 'boolean') {
    params['isDone'] = filters.isDone;
  }
  if (filters.watchers) {
    params['watchers'] = filters.watchers.join(',');
  }
  if (filters.number) {
    params['number'] = filters.number.join(',');
  }
  if (filters.parentNumber) {
    params['parentNumber'] = filters.parentNumber.join(',');
  }
  if (filters.assigneeSubItems) {
    params['assigneeSubItems'] = filters.assigneeSubItems;
  }

  const laneId = filters.laneId;
  if (laneId) {
    params['laneId'] = laneId.join(',');
  }

  const laneCollectionId = filters.laneCollectionId;
  if (laneCollectionId) {
    params['laneCollectionId'] = laneCollectionId.join(',');
  }

  const workTypeId = filters.workTypeId;
  if (workTypeId) {
    params['workTypeId'] = workTypeId.join(',');
  }

  const teamId = filters.teamId;
  if (teamId) {
    params['teamId'] = teamId.join(',');
  }

  const boardId = filters.boardId;
  if (boardId) {
    params['boardId'] = boardId.join(',');
  }

  const cycleRange = filters.cycleRange;
  if (cycleRange) {
    params['cycleRange'] = cycleRange.join(',');
  }

  const hierarchy = filters.hierarchy;
  if (hierarchy) {
    params['hierarchy'] = hierarchy.join(',');
  }

  const aggregations = filters.aggregations;
  if (aggregations) {
    params['aggregations'] = aggregations.join(',');
  }

  const priority = filters.priority;
  if (priority) {
    params['priority'] = priority.join(',');
  }
  if (typeof filters.isBug === 'boolean') {
    params['isBug'] = filters.isBug as boolean;
  }

  // TODO: Need to implement this on the backend
  // if (typeof filters.isDone === 'boolean') {
  //   params['isDone'] = filters.isDone;
  // }

  if (typeof filters.hasBlocker === 'boolean') {
    params['hasBlocker'] = filters.hasBlocker as boolean;
  }

  if (typeof filters.hasNoTeam === 'boolean') {
    params['hasNoTeam'] = filters.hasNoTeam as boolean;
  }

  if (typeof filters.hasNoBoard === 'boolean') {
    params['hasNoBoard'] = filters.hasNoBoard as boolean;
  }

  const assigneeUsername = filters.assigneeUsername;
  if (assigneeUsername) {
    params['assigneeUsername'] = assigneeUsername.join(',');
  }

  const authorUsername = filters.authorUsername;
  if (authorUsername) {
    params['authorUsername'] = authorUsername.join(',');
  }

  const labels = filters.labels;
  if (labels) {
    params['labels'] = labels.join(',');
  }

  if (filters.color) {
    params['color'] = filters.color.join(',');
  }

  const customFields = filters.customFields;
  if (customFields) {
    params['customFields'] = customFields as unknown as
      | string
      | boolean
      | number;
  }

  if (filters.customFieldExists) {
    params['customFieldExists'] = filters.customFieldExists.join(',');
  }

  // TODO: implement this on the backend
  // if (filters.participant) {
  //   params['participant'] = filters.participant.join(',');
  // }

  const sort = filters.sort;
  if (sort) {
    params['sort'] = sort;
  }

  if (filters.limit) {
    params['limit'] = filters.limit;
  }

  if (filters.cursor) {
    params['cursor'] = filters.cursor;
  }

  if (filters.direction) {
    params['direction'] = filters.direction;
  }

  if (filters.isUnassigned || filters.isUnassigned === false) {
    params['isUnassigned'] = filters.isUnassigned;
  }

  if (filters.checkChildren) {
    params['checkChildren'] = filters.checkChildren;
  }

  if (filters.advancedFilters) {
    params['advancedFilters'] = filters.advancedFilters as unknown as
      | string
      | boolean
      | number;
  }

  return params;
}

export function notificationFiltersToQueryParams(
  filters: FindNotificationsData['query']
): {
  [name: string]: string | boolean | number;
} {
  const params: {
    [name: string]: string | boolean | number;
  } = {};

  if (filters?.dateAddedGte) {
    params['dateAddedGte'] = filters.dateAddedGte;
  }

  if (filters?.dateAddedLte) {
    params['dateAddedLte'] = filters.dateAddedLte;
  }

  if (typeof filters?.read === 'boolean') {
    params['read'] = filters.read;
  }

  if (typeof filters?.saved === 'boolean') {
    params['saved'] = filters.saved;
  }

  if (filters?.action) {
    params['action'] = filters.action.join(',');
  }

  if (filters?.teamId) {
    params['teamId'] = filters.teamId.join(',');
  }

  if (filters?.triggerUserId) {
    params['triggerUserId'] = filters.triggerUserId.join(',');
  }

  if (filters?.role) {
    params['role'] = filters.role.join(',');
  }

  if (filters?.limit) {
    params['limit'] = filters.limit;
  }

  if (filters?.cursor) {
    params['cursor'] = filters.cursor;
  }

  params['direction'] = filters?.direction || 'desc';

  return params;
}

export async function duplicateWorkItem(item: PreparedWorkItem) {
  const itemStore = useItemStore();

  try {
    await itemStore.copyWorkItem(item, {
      fields: [CopyFields['*']],
    });
  } catch (error) {
    Notify({
      type: 'error',
      message:
        'There was an error duplicating the item. If this issue persists, please contact support.',
    });
  }
}
